import { computed, onMounted, ref } from 'vue';
import type { DocumentRevision } from '@/entities/Document';
import { useUserStore } from '@/entities/User';
import type { User } from '@/entities/User';
import { positionApi } from '@/entities/Position';
import type { Position } from '@/entities/Position';
import type { ApiResponse } from '@/shared/lib';
import type { EditModalFormData } from './useDocumentEditModal.types';

export type DocumentEditModalCrossFieldProfile = 'internal' | 'external';

export function getUserPositionIds(u: User): number[] {
  return (u.memberships ?? []).flatMap((m: NonNullable<User['memberships']>[number]) => {
    if (m.positionId) return [m.positionId];
    if (m.positions?.length) return m.positions.map((p: { id: number }) => p.id);
    if (m.position?.id) return [m.position.id];
    return [];
  });
}

interface RevisionProps {
  revision: DocumentRevision | null;
}

export interface PositionOption {
  id: number;
  name: string;
}

export function buildDocumentEditPositionOptions(
  allPositions: Position[],
  currentUser: User | null,
  savedPosition?: PositionOption | null
): PositionOption[] {
  const toOption = (p: PositionOption): PositionOption => ({ id: p.id, name: p.name });
  const userPositionIds = new Set(
    currentUser ? getUserPositionIds(currentUser).map(String) : []
  );

  const baseOptions =
    userPositionIds.size === 0
      ? allPositions.map(toOption)
      : allPositions.filter((p) => userPositionIds.has(String(p.id))).map(toOption);

  if (savedPosition && !baseOptions.some((o) => String(o.id) === String(savedPosition.id))) {
    return [toOption(savedPosition), ...baseOptions];
  }
  return baseOptions;
}

export function useDocumentEditModalPositionCollaboratorOptions(
  props: RevisionProps,
  _formData: EditModalFormData
) {
  const userStore = useUserStore();
  const positionsResponse = ref<ApiResponse<Position[]>>({ success: false, data: [] });

  const positionOptions = computed(() =>
    buildDocumentEditPositionOptions(
      positionsResponse.value.data ?? [],
      userStore.user,
      props.revision?.config?.editedByPosition
    )
  );

  const fetchPositions = async () => {
    try {
      const response = await positionApi.getAllPositions({ itemsPerPage: 10000 });
      positionsResponse.value = { success: true, data: response.data ?? [] };
    } catch {
      positionsResponse.value = { success: false, data: [] };
    }
  };

  onMounted(() => {
    fetchPositions();
  });

  return {
    positionsResponse,
    positionOptions,
  };
}

export function getEditModalNotificationCounts(
  formData: EditModalFormData,
  notifiedUsersCount: number
): { memberCount: number; dateCount: number } {
  const memberCount = Math.max(
    (formData.notificationMemberIds ?? []).length,
    notifiedUsersCount
  );
  const dateCount = (formData.notificationDates ?? []).length;
  return { memberCount, dateCount };
}

export function getDocumentEditModalCrossFieldValidationError(
  formData: EditModalFormData,
  memberCount: number,
  dateCount: number,
  profile: DocumentEditModalCrossFieldProfile
): string | null {
  if (profile === 'external') {
    if (formData.hasRevision === 'true' && (formData.externalRevision ?? '').trim() === '') {
      return 'A revisão externa é obrigatória.';
    }
  }
  if (formData.hasValidity !== 'false') {
    if (dateCount > 0 && memberCount === 0) {
      return 'Selecione ao menos um colaborador para ser notificado.';
    }
    if (memberCount > 0 && dateCount === 0) {
      return 'Selecione ao menos uma data de notificação.';
    }
  }
  if (formData.format === 'EL_FS' && (formData.htmRows ?? []).every((r) => !r.local)) {
    return 'Selecione ao menos um local HTM para o formato Eletrônico/Físico.';
  }
  if (
    formData.format === 'EL_FS' &&
    (formData.htmRows ?? []).some((r) => !r.local || r.copies < 1)
  ) {
    return 'Selecione um local HTM e informe uma quantidade de cópias maior que zero para cada linha adicionada.';
  }
  return null;
}

export function documentEditModalCrossFieldsBlockSave(
  formData: EditModalFormData,
  memberCount: number,
  dateCount: number,
  profile: DocumentEditModalCrossFieldProfile
): boolean {
  if (formData.hasValidity !== 'false') {
    if (dateCount > 0 && memberCount === 0) return true;
    if (memberCount > 0 && dateCount === 0) return true;
  }
  if (
    profile === 'external' &&
    formData.hasRevision === 'true' &&
    (formData.externalRevision ?? '').trim() === ''
  ) {
    return true;
  }
  if (formData.format === 'EL_FS' && (formData.htmRows ?? []).every((r) => !r.local)) {
    return true;
  }
  if (
    formData.format === 'EL_FS' &&
    (formData.htmRows ?? []).some((r) => !r.local || r.copies < 1)
  ) {
    return true;
  }
  return false;
}
